import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Instagram, Linkedin, Leaf, ShieldCheck, Award, Beer } from 'lucide-react';
import { horecaBrands } from '../utils/mockData';
import { pushEvent } from '../utils/analytics';
import usePageMeta from '../hooks/usePageMeta';

const API_BASE = '';

const MILD_BOTTLE = 'https://customer-assets-m6fa6gv7.emergentagent.net/job_044e7215-b684-4e8a-bc75-b8b1576b06e1/artifacts/1vxmk0tt_ChatGPT%20Image%20Aug%2023%2C%202026%2C%2012_48_37%20AM.png';
const PROTEIN_BOTTLE = 'https://customer-assets-m6fa6gv7.emergentagent.net/job_044e7215-b684-4e8a-bc75-b8b1576b06e1/artifacts/3dw6oeub_Strong%20Pint.webp';
const MILD_CAN = 'https://customer-assets-m6fa6gv7.emergentagent.net/job_044e7215-b684-4e8a-bc75-b8b1576b06e1/artifacts/50wc7ulv_Mild%20Can.webp';
const STRONG_CAN = 'https://customer-assets-m6fa6gv7.emergentagent.net/job_044e7215-b684-4e8a-bc75-b8b1576b06e1/artifacts/0j707cgb_Strong%20Can.webp';

const PROOF_ITEMS = [
  '1,00,000+ BOTTLES SOLD',
  'RECIPE DEVELOPED BY VLB BERLIN',
  '50+ HORECA PARTNERS',
  'AVAILABLE IN GURGAON · FARIDABAD · PANCHKULA · GOA',
];

const PILLARS = [
  { icon: Leaf, title: 'German Malts', desc: 'Imported Pilsner & Munich malts.' },
  { icon: Award, title: 'Organic Hops', desc: 'German Magnum hops, sourced from certified organic farms.' },
  { icon: ShieldCheck, title: 'Spring Water', desc: 'Brewed with soft natural spring water for a crisp profile.' },
  { icon: CheckCircle, title: 'Clean Label', desc: 'No added sugar. No glycerine. No artificial preservatives.' },
];

const Home = () => {
  usePageMeta({
    title: 'No-Belly Bier | Clean-Brewed German-Style Lager | Gurgaon',
    description: 'A clean-label German-style lager range — Low Calorie Mild Lager <5% ABV and Protein + Collagen Strong Lager <8% ABV. Available across Gurgaon, Faridabad, Panchkula & Goa.',
    path: '/',
  });

  const [form, setForm] = useState({ name: '', email: '', city: '', consent: false, website: '' });
  const [state, setState] = useState({ loading: false, success: false, error: '' });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) { setState({ loading: false, success: false, error: 'Please accept the privacy consent to continue.' }); return; }
    setState({ loading: true, success: false, error: '' });
    try {
      const res = await fetch(`${API_BASE}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, source_page: 'home/community' }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const d = body.detail;
        const msg = Array.isArray(d) ? d.map(x => x.msg || 'Invalid').join(' · ') : (d || 'Something went wrong.');
        throw new Error(msg);
      }
      // Fire submission event ONLY after backend confirms success (response.ok).
      pushEvent('newsletter_submitted', {
        form_name: 'newsletter',
        page_path: '/',
        submission_status: 'success',
      });
      setState({ loading: false, success: true, error: '' });
      setForm({ name: '', email: '', city: '', consent: false, website: '' });
    } catch (err) {
      setState({ loading: false, success: false, error: err.message });
    }
  };

  return (
    <main className="nb-home">

      {/* 1 · HERO */}
      <section className="nb-hero" data-testid="hero">
        <div className="nb-hero-glow" aria-hidden="true"></div>
        <div className="nb-hero-inner">
          <div className="nb-hero-copy">
            <p className="nb-eyebrow" data-testid="hero-eyebrow">CLEAN BREWED. CONSCIOUSLY CRAFTED.</p>
            <h1 className="nb-display" data-testid="hero-headline">GET THE BUZZ.<br />SKIP THE BELLY.</h1>
            <p className="nb-hero-support" data-testid="hero-support">
              Brewed with German malt, organic hops and spring water. No added sugar. No glycerine. No artificial preservatives.
            </p>
            <p className="nb-hero-claim" data-testid="hero-claim">A LIGHTER, CLEAN-BREWED LAGER.</p>
            <div className="nb-hero-ctas">
              <Link
                to="/find-us"
                className="nb-cta-primary nb-cta-lg"
                data-testid="hero-cta-primary"
                onClick={() => pushEvent('find_store_click', { button_location: 'home_hero_primary' })}
              >
                FIND IT NEAR YOU<ArrowRight size={18} />
              </Link>
              <Link
                to="/our-beer"
                className="nb-cta-ghost nb-cta-lg"
                data-testid="hero-cta-secondary"
                onClick={() => pushEvent('explore_beer_click', { button_location: 'home_hero_secondary' })}
              >
                EXPLORE THE BREW
              </Link>
            </div>
          </div>
          <div className="nb-hero-bottle" data-testid="hero-bottle">
            <img
              src={MILD_BOTTLE}
              alt="No-Belly Bier Low Calorie Mild Lager 330ml bottle"
              width="480"
              height="640"
              loading="eager"
              fetchpriority="high"
              decoding="async"
            />
          </div>
        </div>
      </section>

      {/* 2 · PROOF STRIP */}
      <section className="nb-proof" data-testid="proof-strip" aria-label="Proof points">
        <div className="nb-proof-track">
          {PROOF_ITEMS.map((t, i) => (
            <React.Fragment key={i}>
              <span className="nb-proof-item">{t}</span>
              {i < PROOF_ITEMS.length - 1 && <span className="nb-proof-sep" aria-hidden="true">|</span>}
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* 3 · TWO PRINCIPAL VARIANTS */}
      <section className="nb-variants" id="our-beer" data-testid="variants">
        <header className="nb-section-head">
          <p className="nb-eyebrow">OUR BEER</p>
          <h2 className="nb-display-md">TWO EXPRESSIONS. ONE STANDARD.</h2>
        </header>
        <div className="nb-variants-grid">
          <article className="nb-variant" data-testid="variant-lowcalorie-card">
            <div className="nb-variant-image"><img src={MILD_BOTTLE} alt="No-Belly Low Calorie Mild Lager <5% ABV" /></div>
            <div className="nb-variant-body">
              <span className="nb-tag">AVAILABLE</span>
              <h3 className="nb-display-sm">LOW CALORIE<br />MILD LAGER</h3>
              <p className="nb-abv">&lt;5% ABV · MILD LAGER</p>
              <p className="nb-copy">Crisp mild lager brewed with 100% German malts and organic Magnum hops.</p>
              <p className="nb-formats">330ml Pint · 650ml Bottle · 500ml Can</p>
              <Link
                to="/our-beer#mild"
                className="nb-cta-ghost"
                data-testid="variant-mild-more"
                onClick={() => pushEvent('explore_beer_click', { button_location: 'home_variant_mild' })}
              >Details<ArrowRight size={14} /></Link>
            </div>
          </article>

          <article className="nb-variant nb-variant-protein" data-testid="variant-protein-card">
            <div className="nb-variant-image"><img src={PROTEIN_BOTTLE} alt="No-Belly Protein + Collagen Strong Lager <8% ABV" /></div>
            <div className="nb-variant-body">
              <span className="nb-tag nb-tag-red">AVAILABLE</span>
              <h3 className="nb-display-sm">PROTEIN + COLLAGEN<br />STRONG LAGER</h3>
              <p className="nb-abv nb-abv-red">&lt;8% ABV · STRONG LAGER</p>
              <p className="nb-copy">Strong lager brewed with 100% German malts and organic Magnum hops, then infused with hydrolysed protein and vegan collagen.</p>
              <p className="nb-formats">650ml Bottle · 500ml Can</p>
              <Link
                to="/our-beer#protein"
                className="nb-cta-ghost nb-cta-ghost-red"
                data-testid="variant-protein-more"
                onClick={() => pushEvent('explore_beer_click', { button_location: 'home_variant_protein' })}
              >Details<ArrowRight size={14} /></Link>
            </div>
          </article>
        </div>
        <p className="nb-microcopy">Non-Alcoholic 0.0% ABV lager coming soon in 330ml Pints.</p>
      </section>

      {/* 3b · 500 ML CANS SHOWCASE */}
      <section className="nb-cans" data-testid="cans-showcase">
        <header className="nb-section-head">
          <p className="nb-eyebrow">NEW PACKAGING</p>
          <h2 className="nb-display-md">NOW IN 500 ML CANS.</h2>
        </header>
        <div className="nb-cans-row">
          <figure className="nb-can" data-testid="can-mild">
            <img src={MILD_CAN} alt="No-Belly Low Calorie Mild Lager 500ml can" loading="lazy" />
            <figcaption>
              <span className="nb-can-name">LOW CALORIE MILD LAGER</span>
              <span className="nb-can-meta">500 ML · &lt;5% ABV</span>
            </figcaption>
          </figure>
          <figure className="nb-can" data-testid="can-strong">
            <img src={STRONG_CAN} alt="No-Belly Protein + Collagen Strong Lager 500ml can" loading="lazy" />
            <figcaption>
              <span className="nb-can-name">PROTEIN + COLLAGEN STRONG LAGER</span>
              <span className="nb-can-meta">500 ML · &lt;8% ABV</span>
            </figcaption>
          </figure>
        </div>
      </section>

      {/* 4 · FIND US */}
      <section className="nb-find" id="find-us" data-testid="find-us">
        <header className="nb-section-head nb-section-head-centered">
          <p className="nb-eyebrow">FIND US</p>
          <h2 className="nb-display-md">POUR YOURSELF ONE.</h2>
          <p className="nb-copy nb-copy-centered">Stocked at premium liquor stores and 50+ bars, restaurants &amp; hotels across:</p>
        </header>
        <div className="nb-cities">
          {['Gurgaon', 'Faridabad', 'Panchkula', 'Goa'].map(city => (
            <Link
              key={city}
              to={`/find-us?city=${encodeURIComponent(city)}`}
              className="nb-city-btn"
              data-testid={`city-btn-${city.toLowerCase()}`}
              onClick={() => pushEvent('city_filter_selected', { city, button_location: 'home_find_us_cities' })}
            >{city}</Link>
          ))}
        </div>
        <Link
          to="/find-us"
          className="nb-cta-primary nb-cta-lg"
          data-testid="find-us-cta"
          onClick={() => pushEvent('find_store_click', { button_location: 'home_find_us_section' })}
        >Find No-Belly Near You<ArrowRight size={18} /></Link>
      </section>

      {/* 5 · CLEAN BREWING */}
      <section className="nb-pillars" data-testid="pillars">
        <header className="nb-section-head">
          <p className="nb-eyebrow">CLEAN BREWED</p>
          <h2 className="nb-display-md">PREMIUM GERMAN INGREDIENTS. NOTHING UNNECESSARY.</h2>
        </header>
        <div className="nb-pillars-grid">
          {PILLARS.map((p, i) => (
            <div key={i} className="nb-pillar" data-testid={`pillar-${i}`}>
              <p.icon size={28} className="nb-pillar-icon" />
              <h3 className="nb-pillar-title">{p.title}</h3>
              <p className="nb-copy">{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 6 · HORECA LOGO STRIP */}
      <section className="nb-horeca" data-testid="horeca-strip">
        <header className="nb-section-head">
          <p className="nb-eyebrow">POURING AT</p>
          <h2 className="nb-display-md">PREMIUM VENUES ACROSS GURGAON.</h2>
        </header>
        <div className="nb-horeca-grid" data-testid="horeca-logos-grid">
          {horecaBrands.map(b => (
            <div key={b.id} className="nb-horeca-tile" data-testid={`horeca-logo-${b.slug}`}>
              <img src={b.logo} alt={b.name} loading="lazy" />
            </div>
          ))}
          <div className="nb-horeca-tile nb-horeca-tile-more" data-testid="horeca-logo-more" aria-label="And more outlets">
            <span className="nb-horeca-more-line">AND</span>
            <span className="nb-horeca-more-count">30+</span>
            <span className="nb-horeca-more-line">MORE</span>
          </div>
        </div>
      </section>

      {/* 7 · BRAND IN THE WILD */}
      <section className="nb-events" id="events" data-testid="events">
        <header className="nb-section-head">
          <p className="nb-eyebrow">BRAND IN THE WILD</p>
          <h2 className="nb-display-md">WHERE NO-BELLY IS POURING.</h2>
        </header>
        <div className="nb-events-gallery nb-events-gallery-rect" data-testid="events-gallery">
          <div><img src="/events/holi1.jpg" alt="No-Belly on-ground activation" loading="lazy" /></div>
          <div><img src="/events/harley1.jpg" alt="No-Belly on-ground activation" loading="lazy" /></div>
          <div><img src="/events/harley4.jpg" alt="No-Belly on-ground activation" loading="lazy" /></div>
          <div><img src="/events/harley2.jpg" alt="No-Belly on-ground activation" loading="lazy" /></div>
          <div><img src="/events/holi2.jpg" alt="No-Belly on-ground activation" loading="lazy" /></div>
          <div><img src="/events/harley3.jpg" alt="No-Belly on-ground activation" loading="lazy" /></div>
        </div>
        <ul className="nb-events-namelist" data-testid="events-namelist">
          <li>Holi Festival Activation</li>
          <li>Harley Owners Group Meet</li>
          <li>Pickleball Tournament</li>
        </ul>
        <Link to="/events" className="nb-cta-ghost" data-testid="events-more">See All Events<ArrowRight size={14} /></Link>
      </section>

      {/* 8 · FOUNDER STORY */}
      <section className="nb-founders" data-testid="founders">
        <header className="nb-section-head">
          <p className="nb-eyebrow">OUR STORY</p>
          <h2 className="nb-display-md">BUILT BY BEER-DRINKERS. FOR BEER-DRINKERS.</h2>
        </header>
        <div className="nb-founders-row">
          <figure className="nb-founder" data-testid="founder-rahul">
            <img src="/team/rahul.jpg" alt="Rahul Dudeja" />
            <figcaption><strong>Rahul Dudeja</strong><span>Founder &amp; CEO</span></figcaption>
          </figure>
          <p className="nb-founder-story">
            After two decades in India Inc, Rahul &amp; Garima set out to build the beer they always wanted — clean, crisp and honest.
            Every bottle is brewed to the recipe developed with VLB Berlin, without added sugar, glycerine or preservatives.
          </p>
          <figure className="nb-founder" data-testid="founder-garima">
            <img src="/team/garima.png" alt="Garima Mathur" />
            <figcaption><strong>Garima Mathur</strong><span>Co-Founder &amp; CMO</span></figcaption>
          </figure>
        </div>
        <div className="nb-founders-links">
          <a href="https://www.linkedin.com/in/rahul-dudeja-56610823/" target="_blank" rel="noopener noreferrer" className="nb-cta-ghost" data-testid="founder-rahul-linkedin"><Linkedin size={14} /> Rahul on LinkedIn</a>
          <a href="https://www.linkedin.com/in/garima-mathur-2551a396/" target="_blank" rel="noopener noreferrer" className="nb-cta-ghost" data-testid="founder-garima-linkedin"><Linkedin size={14} /> Garima on LinkedIn</a>
          <Link to="/our-story" className="nb-cta-ghost" data-testid="our-story-more">Read the full story<ArrowRight size={14} /></Link>
        </div>
      </section>

      {/* 9 · INSTAGRAM / COMMUNITY */}
      <section className="nb-insta" data-testid="community">
        <div className="nb-insta-copy">
          <p className="nb-eyebrow">JOIN THE COMMUNITY</p>
          <h2 className="nb-display-md">SEE NO-BELLY IN THE WILD.</h2>
          <p className="nb-copy">Fresh drops, activations and events — first on our feed.</p>
          <div className="nb-insta-links">
            <a
              href="https://instagram.com/nobellybier"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => pushEvent('instagram_click', { button_location: 'home_community_brand' })}
              className="nb-cta-primary"
              data-testid="insta-brand"
            ><Instagram size={16} aria-hidden="true" /> @nobellybier</a>
            <a
              href="https://instagram.com/coupleofbiers"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => pushEvent('instagram_click', { button_location: 'home_community_founders' })}
              className="nb-cta-ghost"
              data-testid="insta-founders"
            ><Instagram size={16} aria-hidden="true" /> @coupleofbiers</a>
          </div>
        </div>

        {/* Consumer sign-up */}
        <form onSubmit={submit} className="nb-community-form" data-testid="newsletter-form" noValidate>
          <input type="text" name="website" tabIndex="-1" autoComplete="off"
            value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })}
            style={{ position: 'absolute', left: '-10000px', width: '1px', height: '1px', opacity: 0 }} aria-hidden="true" />
          {state.success ? (
            <div className="nb-form-success" data-testid="newsletter-success"><CheckCircle size={24} /> You're on the list. We'll be in touch.</div>
          ) : (
            <>
              <input
                type="text" placeholder="Full name" required minLength={2} maxLength={80}
                name="name" autoComplete="name" aria-label="Full name"
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="newsletter-name"
              />
              <input
                type="email" placeholder="Email" required
                name="email" autoComplete="email" aria-label="Email"
                value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="newsletter-email"
              />
              <input
                type="text" placeholder="City" required minLength={2} maxLength={60}
                name="city" autoComplete="address-level2" aria-label="City"
                value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                data-testid="newsletter-city"
              />
              <label className="nb-consent">
                <input type="checkbox" required checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} data-testid="newsletter-consent" />
                <span>I am 21+ and agree to the <Link to="/privacy" className="nb-inline-link">Privacy Policy</Link>.</span>
              </label>
              {state.error && <p className="nb-form-error" role="alert" data-testid="newsletter-error">{state.error}</p>}
              <button type="submit" className="nb-cta-primary" disabled={state.loading} data-testid="newsletter-submit">
                {state.loading ? 'Submitting…' : 'Subscribe'}{!state.loading && <ArrowRight size={16} />}
              </button>
            </>
          )}
        </form>
      </section>

      {/* 10 · CONTACT / FOR TRADE */}
      <section className="nb-contact" id="contact" data-testid="contact-strip">
        <div className="nb-contact-card">
          <p className="nb-eyebrow">FOR TRADE</p>
          <h3 className="nb-display-sm">RETAIL &amp; HORECA PARTNERSHIPS</h3>
          <p className="nb-copy">Stock No-Belly at your outlet, bar or hotel.</p>
          <a
            href="mailto:contact@nobelly.in"
            className="nb-cta-primary"
            data-testid="contact-trade"
            onClick={() => pushEvent('trade_contact_click', { button_location: 'home_contact_trade_email' })}
          >contact@nobelly.in</a>
          <Link to="/horeca" className="nb-inline-link" data-testid="for-trade-more">Learn more &rarr;</Link>
        </div>
        <div className="nb-contact-card">
          <p className="nb-eyebrow">FOR CONSUMERS</p>
          <h3 className="nb-display-sm">GENERAL QUERIES</h3>
          <p className="nb-copy">Store availability, feedback or invitations.</p>
          <a
            href="mailto:contact@nobelly.in"
            className="nb-cta-ghost"
            data-testid="contact-consumer"
            onClick={() => pushEvent('consumer_contact_click', { button_location: 'home_contact_consumer_email' })}
          >contact@nobelly.in</a>
          <a
            href="tel:+919999307127"
            className="nb-inline-link"
            data-testid="contact-phone"
            onClick={() => pushEvent('consumer_contact_click', { button_location: 'home_contact_consumer_phone' })}
          >+91 99993 07127</a>
        </div>
      </section>

    </main>
  );
};

export default Home;
